import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CurrentImage, DashboardStatsData, ReportComment } from "@partikelart/portal/shared-typing";
import { Observable, of } from "rxjs";
import { environment } from "../environments/environment";
import { berichtPdfURL, overviewData, pvPdfURL, userData } from "./mock-data";
import { deleteReportComment, getCommentFromLocalStorage, saveCommentToLocalStorage } from "./mock-data-comment";
import {
	getAllReports,
	getBauteilbildForReport,
	getBauteilWithReportData,
	getGridData,
	getReportById,
	reportPartOverviewData,
} from "./mock-reports-getter";

const API = `${environment.portalBackendUrl}/api`;

/** Demo site UUID — matches libs/identity/data-access-user DEMO_SITE_ID and seed.ts. */
const DEMO_SITE_ID = "00000000-0000-4000-8000-000000000010";

function getSiteId(): string {
	return localStorage.getItem("siteIdOption") || DEMO_SITE_ID;
}

/**
 * Demo-mode report filter. The mock report JSON still carries the legacy
 * `customer_no` string; in demo mode the active site is the single seeded
 * demo site, so we return all reports (site scoping is the backend's job in
 * real mode). Kept as a function so future demo data can filter by site_id.
 */
function filterBySite(reports: ReturnType<typeof getAllReports>, _siteId: string) {
	return reports;
}

@Injectable()
export class BackendDemoInterceptor implements HttpInterceptor {
	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (request.method === "OPTIONS") {
			return of(new HttpResponse({ status: 200 }));
		}

		const url = request.url;

		// ── BFF userinfo ──────────────────────────────────────────────────────────
		if (request.method === "GET" && url === `${environment.portalBackendUrl}/auth/userinfo`) {
			return of(new HttpResponse({ status: 200, body: userData }));
		}

		// ── GET /api/reports (paginated list) ────────────────────────────────────
		if (
			request.method === "GET" &&
			url.startsWith(`${API}/reports`) &&
			!url.includes("/reports/by-component/") &&
			!url.includes("/comment") &&
			!url.includes("/bericht") &&
			!url.includes("/bauteilbild") &&
			!url.includes("/partikelbilder")
		) {
			// Check if it's a single report by ID: /api/reports/:id (no query params path)
			const match = url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/([^/?]+)(?:\\?|$)`));
			if (match && !match[1].includes("?")) {
				const id = match[1];
				const report = getReportById(`${environment.url}/report/with_no/${id}`);
				return of(new HttpResponse({ status: 200, body: { data: report } }));
			}
			// Paginated list with query params.
			// The frontend now sends customer_id (number), but mock data uses
			// customer_no (string). For demo mode, fall back to localStorage.
			const reports = filterBySite(getAllReports(), getSiteId());
			return of(
				new HttpResponse({
					status: 200,
					body: { data: reports, total: reports.length, limit: 25, offset: 0 },
				}),
			);
		}

		// ── GET /api/reports/by-component/:artnr ─────────────────────────────────
		if (request.method === "GET" && url.startsWith(`${API}/reports/by-component/`)) {
			const reports = reportPartOverviewData(`${environment.url}/v2/components/${url.split("/by-component/")[1]}`);
			return of(
				new HttpResponse({
					status: 200,
					body: { data: reports, total: reports.length, limit: 25, offset: 0 },
				}),
			);
		}

		// ── GET /api/sites/:siteId/overview ───────────────────────────────────────
		// DashboardComponent's stats signal calls this endpoint. Compute stats
		// from the same mock reports used by the /reports handler above so the
		// dashboard cards show realistic numbers in demo mode.
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/sites/[^/]+/overview$`))) {
			const reportsForUser = filterBySite(getAllReports(), getSiteId());

			const componentFirstDates = new Map<string, Date>();
			for (const report of reportsForUser) {
				const compNo = report.Component_No;
				if (!compNo) continue;
				const reportDate = report.Date_of_Analysis ? new Date(report.Date_of_Analysis) : null;
				if (!reportDate) continue;
				const existing = componentFirstDates.get(compNo);
				if (!existing || reportDate < existing) {
					componentFirstDates.set(compNo, reportDate);
				}
			}
			let lastComponentAddedAt: string | null = null;
			if (componentFirstDates.size > 0) {
				const latestDate = Array.from(componentFirstDates.values()).reduce(
					(max, d) => (d > max ? d : max),
					new Date(0),
				);
				lastComponentAddedAt = latestDate.toISOString();
			}

			let lastReportDate: string | null = null;
			if (reportsForUser.length > 0) {
				const allDates = reportsForUser
					.map((r) => (r.Date_of_Analysis ? new Date(r.Date_of_Analysis) : null))
					.filter((d): d is Date => d !== null);
				if (allDates.length > 0) {
					lastReportDate = allDates.reduce((max, d) => (d > max ? d : max)).toISOString();
				}
			}

			// Count unique components (by Component_No) and NOK reports.
			const uniqueComponents = new Set(reportsForUser.map((r) => r.Component_No).filter(Boolean));
			const nokReports = reportsForUser.filter((r) => r.ioResult === false).length;
			const nokRate = reportsForUser.length > 0 ? (nokReports / reportsForUser.length) * 100 : 0;

			const data: DashboardStatsData = {
				berichte: reportsForUser.length,
				berichte_neu: 2,
				bauteile: uniqueComponents.size,
				bauteile_neu: 1,
				lastComponentAddedAt,
				lastReportDate,
				nokRate: Math.round(nokRate * 10) / 10,
			};
			return of(new HttpResponse({ status: 200, body: data }));
		}

		// ── GET /api/sites/:siteId/overview ───────────────────────────────────────
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/sites/[^/]+/overview`))) {
			const siteId = getSiteId();
			const reportsForUser = filterBySite(getAllReports(), siteId);

			const componentFirstDates = new Map<string, Date>();
			for (const report of reportsForUser) {
				const compNo = report.Component_No;
				if (!compNo) continue;
				const reportDate = report.Date_of_Analysis ? new Date(report.Date_of_Analysis) : null;
				if (!reportDate) continue;
				const existing = componentFirstDates.get(compNo);
				if (!existing || reportDate < existing) {
					componentFirstDates.set(compNo, reportDate);
				}
			}
			let lastComponentAddedAt: string | null = null;
			if (componentFirstDates.size > 0) {
				const latestDate = Array.from(componentFirstDates.values()).reduce(
					(max, d) => (d > max ? d : max),
					new Date(0),
				);
				lastComponentAddedAt = latestDate.toISOString();
			}

			let lastReportDate: string | null = null;
			if (reportsForUser.length > 0) {
				const allDates = reportsForUser
					.map((r) => (r.Date_of_Analysis ? new Date(r.Date_of_Analysis) : null))
					.filter((d): d is Date => d !== null);
				if (allDates.length > 0) {
					lastReportDate = allDates.reduce((max, d) => (d > max ? d : max)).toISOString();
				}
			}

			const data: DashboardStatsData = {
				berichte: reportsForUser.length,
				berichte_neu: 2,
				bauteile: 15,
				bauteile_neu: 1,
				lastComponentAddedAt,
				lastReportDate,
				nokRate: 3.2,
			};
			return of(new HttpResponse({ status: 200, body: data }));
		}

		// ── GET /api/sites/:siteId/parts ──────────────────────────────────────────
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/sites/[^/]+/parts`))) {
			return of(new HttpResponse({ status: 200, body: getGridData(url) }));
		}

		// ── GET /api/users/me (identity shape) ────────────────────────────────────
		// Returned in the identity UsersMeResponse shape so a non-demo-mode client
		// that happens to route through the interceptor still gets a usable body.
		if (request.method === "GET" && url === `${API}/users/me`) {
			return of(
				new HttpResponse({
					status: 200,
					body: {
						id: "00000000-0000-4000-8000-000000000020",
						email: "max@muster-gmbh.de",
						name: "Max Mustermann",
						activeSiteId: DEMO_SITE_ID,
						accounts: [
							{
								accountId: "00000000-0000-4000-8000-000000000001",
								accountDisplayName: "Muster GmbH",
								accountLegalName: "Muster GmbH",
								role: "owner",
								active: true,
								accountDefaultSiteId: DEMO_SITE_ID,
							},
						],
						sitePermissions: [
							{
								siteId: DEMO_SITE_ID,
								siteName: "Musterstadt HQ",
								accountId: "00000000-0000-4000-8000-000000000001",
								permission: "site_admin",
							},
						],
					},
				}),
			);
		}

		// ── GET /api/reports/:id/comment ─────────────────────────────────────────
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/comment`))) {
			const split = url.split("/");
			const id = split[split.indexOf("reports") + 1];
			const comment = getCommentFromLocalStorage(id);
			if (comment.CommentText) {
				return of(new HttpResponse({ status: 200, body: comment }));
			}
			return of(new HttpResponse({ status: 404 }));
		}

		// ── POST /api/reports/:id/comment ────────────────────────────────────────
		if (request.method === "POST" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/comment`))) {
			saveCommentToLocalStorage(request);
			return of(new HttpResponse({ status: 200 }));
		}

		// ── DELETE /api/reports/:id/comment ──────────────────────────────────────
		if (request.method === "DELETE" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/comment`))) {
			const split = url.split("/");
			const id = split[split.indexOf("reports") + 1];
			deleteReportComment(id);
			return of(new HttpResponse({ status: 200 }));
		}

		// ── GET /api/reports/:analysisNo/bericht ─────────────────────────────────
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/bericht`))) {
			return of(new HttpResponse({ status: 200, body: berichtPdfURL }));
		}

		// ── GET /api/reports/:analysisNo/bauteilbild ─────────────────────────────
		if (request.method === "GET" && url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/bauteilbild`))) {
			const analysisNo = url.split("/reports/")[1].split("/")[0];
			return of(
				new HttpResponse({
					status: 200,
					body: getBauteilbildForReport(`${environment.url}/bauteilbild/${analysisNo}`),
				}),
			);
		}

		// ── GET /api/reports/:analysisNo/partikelbilder/:id ──────────────────────
		if (
			request.method === "GET" &&
			url.match(new RegExp(`^${API.replace(/\//g, "\\/")}/reports/[^/]+/partikelbilder/`))
		) {
			const imageId = url.split("/partikelbilder/")[1];
			const imageData: CurrentImage = {
				src: `/assets/images/partikel_${imageId}.jpg`,
				image_id: imageId,
			};
			return of(new HttpResponse({ status: 200, body: imageData }));
		}

		// ── GET /api/pruefvorschriften/:pvNumber ─────────────────────────────────
		if (request.method === "GET" && url.startsWith(`${API}/pruefvorschriften/`)) {
			return of(new HttpResponse({ status: 200, body: pvPdfURL }));
		}

		// ── GET /api/filtersize/:artnr ───────────────────────────────────────────
		if (request.method === "GET" && url.startsWith(`${API}/filtersize/`)) {
			return of(new HttpResponse({ status: 200, body: { filtersize: 5 } }));
		}

		// ── GET /api/specifications ──────────────────────────────────────────────
		if (request.method === "GET" && url.startsWith(`${API}/specifications`)) {
			// GET /api/specifications/report/:id
			if (url.includes("/report/")) {
				return of(
					new HttpResponse({
						status: 200,
						body: { zeichnr: "123345", katseite: "56", zeichindex: "Testteil", artnr: "5123432" },
					}),
				);
			}
			return of(new HttpResponse({ status: 200, body: [] }));
		}

		// ── POST /api/uploads/images/:category ───────────────────────────────────
		if (request.method === "POST" && url.startsWith(`${API}/uploads/images/`)) {
			return of(
				new HttpResponse({
					status: 200,
					body: { message: "ok", data: { blobName: "mock", url: "", category: "mock", fileName: "mock.jpg" } },
				}),
			);
		}

		// ── GET /api/uploads/images/:category ────────────────────────────────────
		if (request.method === "GET" && url.startsWith(`${API}/uploads/images/`)) {
			return of(new HttpResponse({ status: 200, body: { data: [] } }));
		}

		// ── POST .../repeat ──────────────────────────────────────────────────────
		if (request.method === "POST" && url.endsWith("/repeat")) {
			return of(new HttpResponse({ status: 200 }));
		}

		return next.handle(request);
	}
}
